import React, { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { css } from '@emotion/css';
import { useGetBenefitsListPage } from '../../../customHooks/useGetBenefitsListPage.hook';
import { BASE_PATH, KAEUFER_PLUS_PATH, MIETER_PLUS_PATH, WOHNEN_PLUS_PATH } from '../../../domain/routes';
import { BenefitsListPage } from '../../../@types/benefitsListPage';
import type { CustomPageName } from '../../../tracking/tracking';
import { report } from '../../../tracking/tracking';
import { useHasMeinPlus } from '../../../customHooks/useHasMeinPlus.hook';
import { TrackingEvents } from '../../../tracking/trackingEvents';
import { generateQueryParams } from '@/utils/generateQueryParams.util';
import { Msg } from '@/i18n';

const styles = {
  link: css`
    color: var(--color-text-default) !important;
    border-color: #c4c4c4;
    box-shadow: none;
    border-radius: 20px;
    height: 40px;
    &:hover {
      background-color: rgb(231, 253, 241);
      border-color: var(--green-600);
    },
    &:focus {
      color: var(--color-text-default);
    },
    &:active {
      color: var(--color-text-default);
    },
  `,
  activeChip: css`
    text-decoration: none !important;
    cursor: default;
    border-color: var(--green-600);
    background-color: rgb(231, 253, 241);
    &:hover {
      background-color: rgb(231, 253, 241);
    }
  `,
  inactiveChip: {
    textDecoration: 'none !important',
  },
  navigationLinksWrapper: {
    display: 'flex',
    gap: '16px',
  },
};

type NavigationLinks = {
  [key: string]: {
    id: string;
    defaultMessage: string;
    path: string;
    relatedBenefitsPage: BenefitsListPage;
    customPageName: CustomPageName;
  };
};

export const ProductNavigationLinks: React.FC = () => {
  const [searchParams] = useSearchParams();
  const queryParam = generateQueryParams(searchParams);
  const benefitsListPage = useGetBenefitsListPage();
  const isMeinPlus = useHasMeinPlus();
  const handleClick = useCallback(
    (customPageName: CustomPageName) => {
      report(TrackingEvents.LAYOUT, isMeinPlus, customPageName);
    },
    [isMeinPlus],
  );

  const navigationLinks: NavigationLinks = {
    renting: {
      id: `common.navigationLinks.renting`,
      defaultMessage: 'common.navigationLinks.renting',
      path: `${BASE_PATH}${MIETER_PLUS_PATH}${queryParam}`,
      relatedBenefitsPage: BenefitsListPage.MieterPlus,
      customPageName: MIETER_PLUS_PATH,
    },
    living: {
      id: `common.navigationLinks.living`,
      defaultMessage: 'common.navigationLinks.living',
      path: `${BASE_PATH}${WOHNEN_PLUS_PATH}${queryParam}`,
      relatedBenefitsPage: BenefitsListPage.WohnenPlus,
      customPageName: WOHNEN_PLUS_PATH,
    },
    buying: {
      id: `common.navigationLinks.buying`,
      defaultMessage: 'common.navigationLinks.buying',
      path: `${BASE_PATH}${KAEUFER_PLUS_PATH}${queryParam}`,
      relatedBenefitsPage: BenefitsListPage.KauferPlus,
      customPageName: KAEUFER_PLUS_PATH,
    },
  };

  return (
    <div className={css(styles.navigationLinksWrapper)}>
      {Object.keys(navigationLinks).map((key) => {
        const { path, relatedBenefitsPage, defaultMessage, id, customPageName } = navigationLinks[key];
        const navigationLinkClasses =
          relatedBenefitsPage === benefitsListPage
            ? `chip chip--active ${css(styles.activeChip)}`
            : `chip ${css(styles.inactiveChip)}`;
        return (
          <Link
            key={key}
            onClick={() => handleClick(customPageName)}
            to={path}
            className={`${navigationLinkClasses} ${css(styles.link)}`}
            role='presentation'
            aria-label={id}
          >
            <Msg id={id} defaultMessage={defaultMessage} />
          </Link>
        );
      })}
    </div>
  );
};
